import React from 'react';

import { ehPapelFinanceiroExclusivo, exclusivoSelecionado } from '../../../shared/papeis';
import './PerfisAdicionais.css';

/** Perfis oferecidos como adicionais, com o nome que aparece na tela. */
export const PERFIS_DISPONIVEIS: Array<{ valor: string; rotulo: string }> = [
  // Ordem alfabética pelo RÓTULO (pedido do Victor, 25/09/2026).
  { valor: 'ADMINISTRACAO', rotulo: 'Administração' },
  { valor: 'ADMIN', rotulo: 'Administrador' },
  { valor: 'CHEFE_DE_OFICINA', rotulo: 'Chefe de Oficina' },
  { valor: 'COLABORADOR', rotulo: 'Colaborador' },
  { valor: 'COMISSIONAMENTO', rotulo: 'Comissionamento' },
  { valor: 'COMPRA', rotulo: 'Compra' },
  { valor: 'CONSULTOR', rotulo: 'Consultor' },
  { valor: 'CONTAC', rotulo: 'Contac' },
  { valor: 'ESTOQUE', rotulo: 'Estoque' },
  { valor: 'EVENTOS', rotulo: 'Eventos' },
  { valor: 'GESTAO_COMPRAS', rotulo: 'Gestão de Compras' },
  { valor: 'INSPETOR', rotulo: 'Inspetor' },
  { valor: 'TELAO', rotulo: 'Telão' },
  { valor: 'ORCAMENTO', rotulo: 'Orçamento' },
  { valor: 'RASTREADOR', rotulo: 'Rastreador' },
  { valor: 'RECEPCAO', rotulo: 'Recepção' },
  { valor: 'REGIONAL', rotulo: 'Regional' },
  { valor: 'RELACIONAMENTO', rotulo: 'Relacionamento' },
  { valor: 'TI', rotulo: 'TI' },
];

interface Props {
  /** Papel principal (o do select acima) — sempre incluído, e não desmarcável aqui. */
  papelPrincipal: string;
  /** Perfis adicionais marcados. */
  selecionados: string[];
  onChange: (papeis: string[]) => void;
  /** TI não concede o perfil de Administrador (ADR 0012). */
  ocultarAdmin?: boolean;
}

/**
 * Perfis adicionais de um usuário (at-81, ADR 0044).
 *
 * O acesso da pessoa é a UNIÃO do perfil principal com os marcados aqui. Existe porque uma
 * pessoa que acumula duas funções na empresa precisava de dois logins antes desta entrega.
 *
 * ⚠️ A tela desabilita as combinações que o backend recusa (os quatro perfis financeiros são
 * excludentes entre si) — sem isso o erro só apareceria como 400 depois de preencher tudo. A
 * imposição continua sendo do backend; aqui é só para não deixar a pessoa errar.
 */
const PerfisAdicionais: React.FC<Props> = ({
  papelPrincipal,
  selecionados,
  onChange,
  ocultarAdmin = false,
}) => {
  const conjunto = [papelPrincipal, ...selecionados];
  const exclusivo = exclusivoSelecionado(conjunto);

  const alternar = (valor: string) => {
    onChange(
      selecionados.includes(valor)
        ? selecionados.filter((p) => p !== valor)
        : [...selecionados, valor],
    );
  };

  const opcoes = PERFIS_DISPONIVEIS.filter(
    ({ valor }) => valor !== papelPrincipal && !(ocultarAdmin && valor === 'ADMIN'),
  );

  return (
    <div className="perfis-adicionais">
      <div className="CampoTitulo">Perfis adicionais</div>
      <p className="perfis-adicionais-hint">
        Marque outros perfis quando a pessoa acumula funções. O acesso dela passa a ser a soma de
        todos, e vale a partir do próximo login.
      </p>

      <div className="perfis-adicionais-grade">
        {opcoes.map(({ valor, rotulo }) => {
          const bloqueado =
            Boolean(exclusivo) && ehPapelFinanceiroExclusivo(valor) && valor !== exclusivo;

          return (
            <label
              key={valor}
              className="perfis-adicionais-item"
              title={
                bloqueado
                  ? `Não pode ser combinado com o perfil ${exclusivo}: os perfis financeiros mostram os mesmos números de jeitos diferentes.`
                  : undefined
              }
            >
              <input
                type="checkbox"
                checked={selecionados.includes(valor)}
                disabled={bloqueado}
                onChange={() => alternar(valor)}
              />
              <span>{rotulo}</span>
            </label>
          );
        })}
      </div>

      {exclusivo && (
        <p className="perfis-adicionais-hint perfis-adicionais-alerta">
          Contac, Consultor, Regional e Relacionamento não podem ser combinados entre si — os
          quatro mostram os mesmos números de jeitos diferentes.
        </p>
      )}
    </div>
  );
};

export default PerfisAdicionais;
